import {
  defDocumentViewerModule,
  createHostPageRenderSourceDefinition,
  createPDFPageRenderSourceDefinition,
  BrowserViewportServiceLive,
  HostDocumentRendererLive,
  JsonStore,
  PDFDocumentRendererLive,
  SessionJsonStore,
  resolveMuPDFPages,
  type DocumentViewerDefinition,
  type HostPageRenderSourceInput,
  type PDFSourceInput
} from '@st/document-viewer-kit'
import { Clock, Effect, Layer } from 'effect'
import {
  FetchHttpClient,
  HttpClient,
  HttpClientRequest,
  HttpClientResponse
} from 'effect/unstable/http'
import { match } from '@st/pattern'
import { createAnnotationsPlugin } from '@st/document-viewer-kit/plugins/annotations'
import type { Module } from '@st/redux'
import { ST_CONTRACT_VIEWER_PLUGINS, ST_DOC_VIEWER_PLUGINS } from './plugins'
import type { STViewerAnnotation } from './viewer-annotations'
import { IdGenerator, LocalTimeZone } from '~/effects'
import { copyTextToClipboard } from '~/features/copy-to-clipboard'

const ANNOTATIONS_SETTINGS_KEY = 'st:document-viewer:tool-settings'
const ANNOTATIONS_SESSION_KEY = 'st:document-viewer:tool-session'
const VIEWPORT_SETTINGS_KEY = 'st:document-viewer:viewport-settings'

type STDocViewerPluginsInput = {
  annotations?: Record<string, STViewerAnnotation>
}

type STDocViewerSourceInput = PDFSourceInput | HostPageRenderSourceInput
export type STDocViewerDocument = {
  sessionKey: string
  documentId?: string
  source: STDocViewerSourceInput
  pageRotations: Record<string, number>
  deletedPageNumbers: number[]
}

/** Zoom steps offered by every StanfordTax viewer toolbar. */
export const ST_DOC_VIEWER_ZOOM_LEVELS: number[] = [
  8.33, 12.5, 25, 50, 75, 100, 125, 150, 200, 400, 800
].map((percent) => percent / 100)

const createAnnotationMetadata = Effect.gen(function* () {
  const idGenerator = yield* IdGenerator
  const timeZone = yield* LocalTimeZone
  const time = yield* Clock.currentTimeMillis
  return {
    id: yield* idGenerator.generate,
    time: new Date(time).toISOString(),
    timeZone
  }
})

const copyText = (text: string) => copyTextToClipboard(text, 'Copied selected text to clipboard')

const stPdfDocViewerSourceDefinition = createPDFPageRenderSourceDefinition()
const stHostDocViewerSourceDefinition = createHostPageRenderSourceDefinition()
type STDocViewerViewportScope = 'pdf' | 'formation' | 'formation-checklist' | 'contract'
type STDocViewerMode = {
  kind: 'pdf' | 'custom'
  annotations: boolean
  // How the viewer opens a document. Omitted: at actual size, remembering the
  // user's zoom. `fitToWidth`: fitted until the user zooms, remembering their
  // zoom. `fitToWidthEveryOpen`: fitted on every open until the user zooms,
  // never remembering it.
  zoom?: 'fitToWidth' | 'fitToWidthEveryOpen'
}

const { Overlay: AnnotationsOverlay, ...stPdfDocViewerAnnotationsPlugin } =
  createAnnotationsPlugin<STDocViewerPluginsInput>({
    tools: ST_DOC_VIEWER_PLUGINS,
    initialTool: 'select',
    settingsKey: `${ANNOTATIONS_SETTINGS_KEY}:pdf`,
    storageNamespace: `${ANNOTATIONS_SESSION_KEY}:pdf`,
    createAnnotationMetadata,
    copyText
  })

const stFormationDocViewerAnnotationsPlugin = createAnnotationsPlugin<STDocViewerPluginsInput>({
  tools: ST_DOC_VIEWER_PLUGINS,
  initialTool: 'caret',
  settingsKey: `${ANNOTATIONS_SETTINGS_KEY}:formation`,
  storageNamespace: `${ANNOTATIONS_SESSION_KEY}:formation`,
  createAnnotationMetadata,
  copyText
})

const stContractDocViewerAnnotationsPlugin = createAnnotationsPlugin<STDocViewerPluginsInput>({
  tools: ST_CONTRACT_VIEWER_PLUGINS,
  initialTool: 'select',
  settingsKey: `${ANNOTATIONS_SETTINGS_KEY}:contract`,
  storageNamespace: `${ANNOTATIONS_SESSION_KEY}:contract`,
  createAnnotationMetadata,
  copyText
})
export {
  AnnotationsOverlay,
  stPdfDocViewerAnnotationsPlugin,
  stFormationDocViewerAnnotationsPlugin
}

function buildViewportSettingsKey(scope: STDocViewerViewportScope) {
  return `${VIEWPORT_SETTINGS_KEY}:${scope}`
}

function makeSTDocViewerDefinition(scope: STDocViewerViewportScope, mode: STDocViewerMode) {
  const enableStructuredText = mode.kind == 'pdf' && mode.annotations
  const annotationsPlugin = (() => {
    if (scope == 'contract') return stContractDocViewerAnnotationsPlugin
    if (mode.kind == 'pdf') return stPdfDocViewerAnnotationsPlugin
    return stFormationDocViewerAnnotationsPlugin
  })()

  return {
    source: mode.kind == 'pdf' ? stPdfDocViewerSourceDefinition : stHostDocViewerSourceDefinition,
    resolveSource: (document) => ({
      ...document.source,
      sessionKey: document.sessionKey,
      documentId: document.documentId,
      ...(mode.kind == 'pdf' ? { enableStructuredText } : {})
    }),
    resolvePages: (rawPages, document) =>
      mode.kind == 'pdf'
        ? resolveMuPDFPages({
            pages: rawPages,
            pageRotations: document.pageRotations,
            deletedPageNumbers: document.deletedPageNumbers
          })
        : rawPages,
    plugins: mode.annotations ? [annotationsPlugin] : [],
    viewport: match(mode.zoom)
      .returnType<
        DocumentViewerDefinition<STDocViewerPluginsInput, STDocViewerDocument>['viewport']
      >()
      .with('fitToWidthEveryOpen', () => ({ initialZoomMode: 'fitToWidth' as const }))
      .otherwise((zoom) => ({
        initialZoomMode: zoom ?? 'actualSize',
        settingsKey: buildViewportSettingsKey(scope)
      }))
  } satisfies DocumentViewerDefinition<STDocViewerPluginsInput, STDocViewerDocument>
}

function makeSTDocViewerModule(scope: STDocViewerViewportScope, mode: STDocViewerMode) {
  const definition = makeSTDocViewerDefinition(scope, mode)
  return defDocumentViewerModule<STDocViewerDocument, STDocViewerPluginsInput>(definition)
}

export const stPdfDocViewerModule = makeSTDocViewerModule('pdf', { kind: 'pdf', annotations: true })
export const stFormationDocViewerModule = makeSTDocViewerModule('formation', {
  kind: 'custom',
  annotations: true,
  zoom: 'fitToWidth'
})
export const stFormationChecklistDocViewerModule = makeSTDocViewerModule('formation-checklist', {
  kind: 'custom',
  annotations: false
})
export const stContractDocViewerModule = makeSTDocViewerModule('contract', {
  kind: 'pdf',
  annotations: true,
  zoom: 'fitToWidthEveryOpen'
})

export const stFolderDocViewerLayer = Layer.mergeAll(
  stPdfDocViewerModule.layer,
  stFormationDocViewerModule.layer,
  stFormationChecklistDocViewerModule.layer
)

/**
 * Document URLs are stable API routes that authorize with the session cookie.
 * The viewer fetches PDF bytes without credentials, and a credentialed
 * cross-origin redirect would reach storage with an opaque origin, so each
 * fetch first asks the API for the document's presigned storage URL.
 */
const documentHttpClientLive = Layer.effect(
  HttpClient.HttpClient,
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient
    return client.pipe(
      HttpClient.mapRequestEffect((request) =>
        client.get(request.url, { headers: { accept: 'application/json' } }).pipe(
          Effect.flatMap(HttpClientResponse.filterStatusOk),
          Effect.flatMap((response) => response.json),
          Effect.map((body) => HttpClientRequest.setUrl(request, (body as { url: string }).url)),
          Effect.provideService(FetchHttpClient.RequestInit, { credentials: 'include' })
        )
      )
    )
  })
).pipe(Layer.provide(FetchHttpClient.layer))

/** Physical viewer services belong to the application root, not feature modules. */
export const stDocViewerLiveLayer = Layer.mergeAll(
  JsonStore.LocalStorage,
  SessionJsonStore.Memory,
  BrowserViewportServiceLive,
  PDFDocumentRendererLive.pipe(Layer.provide(documentHttpClientLive)),
  HostDocumentRendererLive
)

export type STDocViewerRuntimeModule = typeof stPdfDocViewerModule

export type STDocViewerRuntimeState = STDocViewerRuntimeModule extends Module<
  infer State,
  any,
  any,
  any
>
  ? State
  : never

export type STDocViewerRuntimeMessage = STDocViewerRuntimeModule extends Module<
  any,
  infer Message,
  any,
  any
>
  ? Message
  : never
